import type { AxiosError, AxiosResponse } from 'axios'
import qs from 'qs'

import request from '../requesters/accounting'
import requestV2 from '../requesters/accountingV2'
import { GraphqlException } from '../resolvers/util'

export const getLedgersByCaseId = async (input: QueryGetLedgersByCaseIdArgs) => {
  const response = await request.get<CaseLedgers>(`/accounting/ledger/case/${input.caseId}`)
  return response.data
}

export const getLedgerFilters = async ({ input }: QueryGetLedgerFiltersArgs) => {
  const {
    org_ids,
    user_id,
    ledger_type,
    has_outstanding_commissions,
    period_id,
    period_start_date,
    period_end_date,
    include_clawback_advisor_in_source,
  } = input ?? {}

  const queryParams: Record<string, unknown> = { org_id: org_ids, user_id }

  // Arrays are serialised as repeated bare keys (ledger_type=A&ledger_type=B)
  if (ledger_type !== undefined) queryParams.ledger_type = ledger_type
  if (has_outstanding_commissions !== undefined) queryParams.has_outstanding_commissions = has_outstanding_commissions
  if (period_id !== undefined) queryParams.period_id = period_id
  if (period_start_date !== undefined) queryParams.period_start_date = period_start_date
  if (period_end_date !== undefined) queryParams.period_end_date = period_end_date
  if (include_clawback_advisor_in_source !== undefined) {
    queryParams.include_clawback_advisor_in_source = include_clawback_advisor_in_source
  }

  const queryString = qs.stringify(queryParams, { arrayFormat: 'repeat' })

  const response: AxiosResponse<LedgerFilters> = await request.get<LedgerFilters>(
    `/accounting/ledger/filter${queryString ? `?${queryString}` : ''}`,
  )
  return response.data
}

interface RawProviderStatementFilters {
  provider_ids?: string[]
  club_ids?: string[]
}

export const getProviderStatementFilters = async ({ input }: QueryGetProviderStatementFiltersArgs) => {
  const { provider_ids, club_ids, period_ids, statuses } = input ?? {}

  const queryString = qs.stringify({ provider_ids, club_ids, period_ids, statuses }, { arrayFormat: 'repeat' })

  const response: AxiosResponse<RawProviderStatementFilters> = await requestV2.get<RawProviderStatementFilters>(
    `/accounting/statement/filter${queryString ? `?${queryString}` : ''}`,
  )

  // Map the raw REST response (snake_case id arrays) onto the GraphQL parent shape. Keep this
  // mapping isolated so a backend contract change is a single-line edit here — the id arrays are
  // hydrated into Organisations by the ProviderStatementFilters type resolver.
  return {
    providerIds: response.data.provider_ids ?? [],
    clubIds: response.data.club_ids ?? [],
  }
}

export const getLedgerSummary = async (input: QueryGetLedgerSummaryArgs) => {
  // extracted query parameters are sent as URL params
  const { org_id = [], user_id = [], totals_only, advisor_ids, if_due, ...bodyFilter } = input.input.filter || {}

  // build query parameters object excluding undefined/empty values
  const queryParams: Partial<LedgerSummaryFilterInput> = {
    totals_only: totals_only ?? false,
  }

  // you can filter by either user_id, advisor_ids or org_id but only one at a time
  if (user_id?.length) {
    queryParams.user_id = user_id
  } else if (advisor_ids?.length) {
    queryParams.user_id = advisor_ids
  } else if (org_id?.length) {
    queryParams.org_id = org_id
  }
  if (totals_only !== undefined) queryParams.totals_only = totals_only
  if (if_due !== undefined) queryParams.if_due = if_due

  const queryString =
    Object.keys(queryParams).length > 0 ? `?${qs.stringify(queryParams, { arrayFormat: 'repeat' })}` : ''

  const requestBody = {
    ...input.input,
    filter: Object.keys(bodyFilter).length > 0 ? bodyFilter : undefined,
  }

  try {
    const response: AxiosResponse<GetLedgerSummaryResponse> = await request.post<GetLedgerSummaryResponse>(
      `/accounting/ledger/summary${queryString}`,
      requestBody,
    )

    return response.data
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    throw new Error(`Failed to fetch ledger summary: ${errorMessage}`)
  }
}

export const getLedgers = async (input: QueryGetLedgersArgs) => {
  // Org ids is also a type of filter but BE accepts it as a query param instead of body
  const { orgIds = [], regulated_by, ...rest } = input.input.filter || {}

  const queryObj = {
    // If no org ids, then we don't wanna query by orgIds
    org_id: orgIds?.length ? orgIds : undefined,
    regulated_by,
    user_id: input.input.user_id,
  }

  const query = qs.stringify(queryObj, { arrayFormat: 'repeat' })

  const response = await request.post<GetLedgersResponse>(`/accounting/ledger${query ? '?' : ''}${query}`, {
    ...input.input,
    filter: rest,
  })
  return response.data
}

interface RawProviderStatement {
  id: number
  amount?: number
  provider_id?: string
  provider_name?: string
  club_name?: string
  period_id?: string
  status?: string
  number_of_transactions?: number
  number_of_items_to_match?: number
}

interface RawGetProviderStatementsResponse {
  statements?: RawProviderStatement[]
  next_page?: { bookmark?: string; pageSize?: number; page_size?: number }
}

export const getProviderStatements = async (
  input: QueryGetProviderStatementsArgs,
): Promise<GetProviderStatementsResponse> => {
  const response = await requestV2.post<RawGetProviderStatementsResponse>('/accounting/statement/provider', input.input)

  return {
    statements: (response.data.statements ?? []).map((statement) => ({
      id: String(statement.id),
      provider_id: statement.provider_id,
      provider_name: statement.provider_name,
      club_name: statement.club_name,
      period_id: statement.period_id,
      status: statement.status,
      total_amount: statement.amount,
      num_transactions: statement.number_of_transactions,
      items_to_match: statement.number_of_items_to_match,
    })),
    nextPage: response.data.next_page,
  }
}

// IDs in accounting are returned as numbers, not strings
export const deleteLedger = async (id: number) => {
  const response = await request.delete(`/accounting/ledger/${id}`, {
    validateStatus: function (status) {
      return status >= 200 && status < 500 // default
    },
  })

  // Accounting microservice doesn't throw error on a status < 500,
  // hence the need to throw the error from the data here
  if (response?.data?.error) {
    throw new GraphqlException(response.data.error)
  }

  return id
}

export const writeOffLedger = async (input: MutationWriteOffLedgerArgs) => {
  const response: AxiosResponse<Ledger> = await request.patch<Ledger>(
    `/accounting/ledger/${input.ledgerId}/write_off`,
    input.input,
  )
  return response.data
}

export const editLedgerPayment = async (input: MutationEditLedgerPaymentArgs) => {
  const response: AxiosResponse<Ledger> = await request.put<Ledger>(
    `/accounting/ledger/${input.ledgerId}/payment`,
    input.input,
  )
  return response.data
}

export const updatePayment = async (input: MutationUpdatePaymentArgs) => {
  const response = await request.patch<LedgerPayment>(
    `/accounting/ledger/${input.ledgerId}/payment/${input.paymentId}`,
    input.input,
  )
  return response.data
}

export const updateLedger = async (input: MutationUpdateLedgerPaymentArgs) => {
  const response = await request.patch<Ledger>(`/accounting/ledger/${input.ledgerId}`, input.input)
  return response.data
}

export const updateLedgersFullPayment = async ({ input }: MutationUpdateLedgersFullPaymentArgs) => {
  try {
    const response = await request.put<PutLedgersFullPaymentResponse>('/accounting/ledger/full_payment', input)
    return response.data
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    throw new Error(`Failed to update ledger(s) full payment: ${errorMessage}`)
  }
}

export const createRecurringPayment = async (input: MutationCreateRecurringPaymentArgs) => {
  const response = await request.post<RecurringLedger>(`/accounting/ledger/recurring`, input.input)
  return response.data
}

export const updateRecurringLedgerPayment = async (input: MutationUpdateRecurringLedgerPaymentArgs) => {
  const response = await request.patch<RecurringLedger>(`/accounting/ledger/recurring/${input.ledgerId}`, input.input)
  return response.data
}

export const createLedgerPayment = async (input: MutationCreateLedgerPaymentArgs) => {
  const response = await request.post<OneOffLedger>(`/accounting/ledger/create`, input.input)
  return response.data
}

export const initiateGoCardlessPayment = async (input: MutationInitiateGoCardlessPaymentArgs) => {
  try {
    // The backend supports multiple client_id parameters using collectionFormat: "multi"
    // We'll build the URL with multiple client_id params from the clientIds array
    const clientIds = input.input?.clientIds || []
    if (clientIds.length === 0) {
      throw new Error('At least one client ID is required')
    }

    const clientIdParams = clientIds.map((id) => `client_id=${encodeURIComponent(id)}`).join('&')
    const response = await request.post(
      `/accounting/go-cardless/initiate-payment?${clientIdParams}&ledger=${input.input?.ledgerID}`,
      undefined,
    )
    return response.data
  } catch (e) {
    const ex = e as AxiosError<{ error: string }>
    if (ex.response?.data?.error) {
      throw new Error(ex.response.data?.error)
    }
    throw new Error('Could not initiate payment')
  }
}

export const getGoCardlessAuthURL = async () => {
  try {
    const response = await request.get(`/accounting/go-cardless/generate-auth-url`)
    return response.data
  } catch (e) {
    const ex = e as AxiosError<{ error: string }>
    if (ex.response?.data?.error) {
      throw new Error(ex.response.data?.error)
    }
    throw new Error('Could not generate authorisation URL')
  }
}

export const getGoCardlessAuthCallback = async (input: MutationGoCardlessAuthCallbackArgs) => {
  if (!input.input?.code || !input.input?.state) {
    throw new Error('Invalid parameters')
  }
  try {
    await request.get(`/accounting/go-cardless/auth-callback?code=${input.input?.code}&state=${input.input?.state}`)
    return 'OK'
  } catch (e) {
    const ex = e as AxiosError<{ error: string }>
    if (ex.response?.data?.error) {
      throw new Error(ex.response.data?.error)
    }
    throw new Error('Could not receive callback')
  }
}

export const getGoCardlessAccountStatus = async () => {
  try {
    const response = await request.get(`/accounting/go-cardless/account-status`)
    return response.data
  } catch (e) {
    const ex = e as AxiosError<{ error: string }>
    if (ex.response?.data?.error) {
      throw new Error(ex.response.data?.error)
    }
    throw new Error('Could not get account status')
  }
}

export const hasGoCardlessMandate = async (input: QueryHasGoCardlessMandateArgs) => {
  if (!input.client_id) {
    throw new Error('Invalid parameters')
  }
  try {
    const response = await request.get(`/accounting/go-cardless/has-mandate?client_id=${input.client_id}`)
    return response.data
  } catch (e) {
    const ex = e as AxiosError<{ error: string }>
    if (ex.response?.data?.error) {
      throw new Error(ex.response.data?.error)
    }
    throw new Error('Could not get mandate status')
  }
}
